import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, Pencil, Trash2, ChevronLeft, ChevronRight, MapPin, ExternalLink, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const TYPE_LABELS = { preventiva: "Preventiva", correctiva: "Correctiva", instalacion: "Instalación" };
const TYPE_STYLES = {
  preventiva: "bg-blue-100 text-blue-800 border-blue-200",
  correctiva: "bg-amber-100 text-amber-800 border-amber-200",
  instalacion: "bg-green-100 text-green-800 border-green-200",
};
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const EMPTY = { technician_id: "", client_id: "", scheduled_at: "", visit_type: "preventiva", location: "", notes: "" };

const pad = (n) => String(n).padStart(2, "0");
const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function monthCells(cursor) {
  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const first = new Date(y, m, 1);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(y, m, 1 - offset);
  return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
}

function weekDays(cursor) {
  const offset = (cursor.getDay() + 6) % 7;
  const monday = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() - offset);
  return Array.from({ length: 7 }, (_, i) => new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i));
}

function VisitChip({ visit, onClick }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(visit); }}
      data-testid={`visit-chip-${visit.id}`}
      className={`block w-full truncate rounded border px-1.5 py-0.5 text-left text-xs font-medium transition-opacity duration-200 hover:opacity-80 ${TYPE_STYLES[visit.visit_type] || TYPE_STYLES.preventiva}`}
      title={`${visit.scheduled_at.slice(11, 16)} · ${visit.client_name} · ${visit.technician_name}`}
    >
      {visit.scheduled_at.slice(11, 16)} {visit.client_name}
    </button>
  );
}

export default function Visits() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [visits, setVisits] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [clients, setClients] = useState([]);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/visits");
      setVisits(data);
    } catch (e) {
      toast.error(apiError(e, "No se pudieron cargar las visitas"));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isAdmin) {
      api.get("/users/technicians").then((r) => setTechnicians(r.data)).catch(() => {});
      api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    }
  }, [isAdmin]);

  const visitsByDay = useMemo(() => {
    const map = {};
    (visits || []).forEach((v) => {
      const key = (v.scheduled_at || "").slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(v);
    });
    return map;
  }, [visits]);

  const days = view === "month" ? monthCells(cursor) : weekDays(cursor);
  const todayKey = dayKey(new Date());

  const moveCursor = (dir) => {
    setCursor((c) => {
      const d = new Date(c);
      if (view === "month") return new Date(d.getFullYear(), d.getMonth() + dir, 1);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate() + dir * 7);
    });
  };

  const headerTitle = view === "month"
    ? `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`
    : `Semana del ${dayKey(days[0]).split("-").reverse().join("/")} al ${dayKey(days[6]).split("-").reverse().join("/")}`;

  const openCreate = () => {
    setForm(EMPTY);
    setEditTarget(null);
    setFormOpen(true);
  };

  const openEdit = (v) => {
    setForm({
      technician_id: v.technician_id,
      client_id: v.client_id,
      scheduled_at: (v.scheduled_at || "").slice(0, 16),
      visit_type: v.visit_type,
      location: v.location || "",
      notes: v.notes || "",
    });
    setEditTarget(v);
    setDetailTarget(null);
    setFormOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.technician_id || !form.client_id || !form.scheduled_at) {
      toast.error("Complete técnico, cliente y fecha/hora");
      return;
    }
    setSaving(true);
    try {
      if (editTarget) {
        await api.put(`/visits/${editTarget.id}`, form);
        toast.success("Visita actualizada");
      } else {
        const { data } = await api.post("/visits", form);
        toast.success(`Visita creada · Orden ${data.order_number} generada y notificada por correo`);
      }
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err, "No se pudo guardar la visita"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/visits/${deleteTarget.id}`);
      toast.success("Visita eliminada");
      setDeleteTarget(null);
      setDetailTarget(null);
      load();
    } catch (err) {
      toast.error(apiError(err, "No se pudo eliminar la visita"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="visits-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => moveCursor(-1)} data-testid="calendar-prev">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="calendar-today">
            Hoy
          </Button>
          <Button variant="outline" size="icon" onClick={() => moveCursor(1)} data-testid="calendar-next">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <h2 className="ml-2 font-heading text-lg font-bold text-slate-900" data-testid="calendar-title">{headerTitle}</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-md border border-slate-200 bg-white p-0.5">
            <button
              onClick={() => setView("month")}
              data-testid="calendar-month-view-button"
              className={`rounded px-3 py-1.5 text-xs font-medium transition-colors duration-200 ${view === "month" ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              Mensual
            </button>
            <button
              onClick={() => setView("week")}
              data-testid="calendar-week-view-button"
              className={`rounded px-3 py-1.5 text-xs font-medium transition-colors duration-200 ${view === "week" ? "bg-primary text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              Semanal
            </button>
          </div>
          {isAdmin && (
            <Button onClick={openCreate} className="gap-2 bg-primary hover:bg-[#0353E9]" data-testid="new-visit-button">
              <Plus className="h-4 w-4" /> Nueva Visita
            </Button>
          )}
        </div>
      </div>

      {visits === null ? (
        <Skeleton className="h-[480px] w-full rounded-lg" />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white" data-testid="calendar-grid">
          <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
            {DAYS.map((d) => (
              <div key={d} className="px-2 py-2 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">
                {d}
              </div>
            ))}
          </div>
          <div className={`grid grid-cols-7 ${view === "month" ? "" : "divide-x divide-slate-100"}`}>
            {days.map((day) => {
              const key = dayKey(day);
              const dayVisits = visitsByDay[key] || [];
              const isToday = key === todayKey;
              const isOtherMonth = view === "month" && day.getMonth() !== cursor.getMonth();
              return (
                <div
                  key={key}
                  data-testid={`calendar-day-${key}`}
                  className={`${view === "month" ? "min-h-[96px] border-b border-r border-slate-100" : "min-h-[320px]"} p-1.5 ${
                    isOtherMonth ? "bg-slate-50/60" : ""
                  } ${isToday ? "bg-blue-50/60" : ""}`}
                >
                  <div className={`mb-1 text-right text-xs font-semibold ${isToday ? "text-primary" : isOtherMonth ? "text-slate-300" : "text-slate-500"}`}>
                    {view === "week" ? `${DAYS[(day.getDay() + 6) % 7]} ${day.getDate()}` : day.getDate()}
                  </div>
                  <div className="space-y-1">
                    {dayVisits.map((v) => (
                      <VisitChip key={v.id} visit={v} onClick={setDetailTarget} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-4 text-xs text-slate-500">
        {Object.entries(TYPE_LABELS).map(([k, label]) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className={`h-2.5 w-2.5 rounded-sm border ${TYPE_STYLES[k]}`} /> {label}
          </span>
        ))}
      </div>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editTarget ? "Editar Visita" : "Nueva Visita Técnica"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="visit-form">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Técnico responsable *</Label>
                <Select value={form.technician_id} onValueChange={(v) => setForm((f) => ({ ...f, technician_id: v }))}>
                  <SelectTrigger data-testid="visit-technician-select"><SelectValue placeholder="Seleccionar técnico" /></SelectTrigger>
                  <SelectContent>
                    {technicians.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Cliente *</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))}>
                  <SelectTrigger data-testid="visit-client-select"><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Fecha y hora *</Label>
                <Input
                  type="datetime-local" required
                  value={form.scheduled_at}
                  onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))}
                  data-testid="visit-datetime-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Tipo de visita</Label>
                <Select value={form.visit_type} onValueChange={(v) => setForm((f) => ({ ...f, visit_type: v }))}>
                  <SelectTrigger data-testid="visit-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preventiva">Preventiva</SelectItem>
                    <SelectItem value="correctiva">Correctiva</SelectItem>
                    <SelectItem value="instalacion">Instalación</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Ubicación</Label>
              <Input
                value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                placeholder="Dirección o punto de encuentro"
                data-testid="visit-location-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Notas</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                data-testid="visit-notes-input"
              />
            </div>
            {!editTarget && (
              <p className="rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
                Al crear la visita se generará automáticamente una orden de servicio pendiente asignada al mismo técnico y se notificará por correo.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-primary hover:bg-[#0353E9]" data-testid="visit-form-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editTarget ? "Guardar cambios" : "Programar visita"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(detailTarget)} onOpenChange={() => setDetailTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading" data-testid="visit-detail-title">
              Visita {detailTarget && TYPE_LABELS[detailTarget.visit_type]}
            </DialogTitle>
          </DialogHeader>
          {detailTarget && (
            <div className="space-y-3" data-testid="visit-detail">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-slate-500">Cliente</p>
                  <p className="font-medium text-slate-900">{detailTarget.client_name}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-slate-500">Técnico</p>
                  <p className="font-medium text-slate-900">{detailTarget.technician_name}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-slate-500">Fecha y hora</p>
                  <p className="font-medium text-slate-900">{(detailTarget.scheduled_at || "").replace("T", " ").slice(0, 16)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-slate-500">Orden vinculada</p>
                  <button
                    onClick={() => navigate(`/orders/${detailTarget.order_id}`)}
                    className="inline-flex items-center gap-1 font-mono font-semibold text-primary hover:underline"
                    data-testid="visit-order-link"
                  >
                    {detailTarget.order_number} <ExternalLink className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              {detailTarget.location && (
                <p className="flex items-center gap-1.5 text-sm text-slate-600">
                  <MapPin className="h-4 w-4 text-slate-400" /> {detailTarget.location}
                </p>
              )}
              {detailTarget.notes && (
                <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700">{detailTarget.notes}</p>
              )}
              {isAdmin && (
                <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => openEdit(detailTarget)} data-testid="visit-edit-button">
                    <Pencil className="h-4 w-4" /> Editar
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50"
                    onClick={() => setDeleteTarget(detailTarget)}
                    data-testid="visit-delete-button"
                  >
                    <Trash2 className="h-4 w-4" /> Eliminar
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">¿Eliminar esta visita?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará la visita programada. La orden de servicio vinculada ({deleteTarget?.order_number}) se conservará.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-visit-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-visit-confirm"
            >
              {saving ? "Eliminando..." : "Eliminar visita"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
